import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SearchPage.css';
import CompactBudgetCard from './CompactBudgetCard';
import BudgetListItem from './BudgetListItem';
import BudgetDetailModal from './BudgetDetailModal';
import SaveSearchModal from './SaveSearchModal';
import BookmarkModal from './BookmarkModal';
import UnifiedButton from './UnifiedButton';
import { ChevronDown, FileBarChart, Bookmark, MoreHorizontal, Search, X } from 'lucide-react';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

// Types
interface SearchFilters {
  organization: string;
  account: string;
  program: string;
  domain: string;
}

// Updated interfaces to match backend nested structure
interface FiscalData {
  value: number;
  fiscal_year: number;
  fiscal_type: string;
}

interface CostItem {
  cost_item_name: string;
  standard_name?: string;
  spending_category?: string;
  description?: string;
  scost_item_supplier?: string;
  fiscal_data: FiscalData[];
}

interface Project {
  pjid: number;
  project_name: string;
  project_type?: string;
  project_description?: string;
  cost_items: CostItem[];
}

interface SearchResult {
  // Line Item Level
  id: string;
  document_id?: number;
  peid?: number;
  line_item?: string;
  line_item_title: string;
  line_item_description?: string;
  
  // Organizational Info
  organization: string;
  account?: string;
  account_title?: string;
  budget_activity_title?: string;
  program?: string;
  domain?: string;
  supplier?: string;
  
  // Nested Structure
  projects: Project[];
  
  // Aggregated Financial Data
  total_budget?: number;
  fiscal_years: number[];
  
  // Search Metadata
  relevance_score?: number;
  highlight?: Record<string, string[]>;
  
  // Legacy fields for backward compatibility
  title: string;
  description: string;
  agency: string;
  budget?: number;
  category: string;
  status: string;
}

// Filter options interface
interface FilterOptions {
  organizations: string[];
  accounts: string[];
  programs: string[];
  domains: string[];
}

// No default constants needed - all options come from API

const SearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    organization: 'All',
    account: 'All',
    program: 'All',
    domain: 'All'
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    organizations: [],
    accounts: [],
    programs: [],
    domains: []
  });
  const [allResults, setAllResults] = useState<SearchResult[]>([]); // All fetched results
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // Current page of batches (100 records each)
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // New state for selection functionality
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [resultsPerPage, setResultsPerPage] = useState(20); // Client-side display limit
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card'); // View mode state
  const [darkMode, setDarkMode] = useState(false); // Dark mode state
  
  // Search control panel states
  const [searchMode, setSearchMode] = useState<'fuzzy' | 'exact'>('fuzzy');
  const [searchScope, setSearchScope] = useState<'all' | 'line_items' | 'projects' | 'cost_items'>('all');
  const [queryType, setQueryType] = useState<'or' | 'and'>('or');
  
  // Modal state
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Save Search Modal state
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [bookmarkModalData, setBookmarkModalData] = useState<{peid: number, title: string} | null>(null);
  
  // Bookmarks state
  const [bookmarkedPeids, setBookmarkedPeids] = useState<Set<number>>(new Set());

  // Computed: Get current page of results to display
  const displayedResults = allResults.slice(0, resultsPerPage);

  // Fetch filter options on component mount
  useEffect(() => {
    fetchFilterOptions();
    loadBookmarks(); // Load user bookmarks
    
    // Check for saved search parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    const filtersParam = urlParams.get('filters');
    
    if (query) {
      setSearchQuery(query);
      
      if (filtersParam) {
        try {
          const savedFilters = JSON.parse(filtersParam);
          
          // Apply saved filters
          if (savedFilters.organization) setFilters(prev => ({ ...prev, organization: savedFilters.organization }));
          if (savedFilters.account) setFilters(prev => ({ ...prev, account: savedFilters.account }));
          if (savedFilters.program) setFilters(prev => ({ ...prev, program: savedFilters.program }));
          if (savedFilters.domain) setFilters(prev => ({ ...prev, domain: savedFilters.domain }));
          
          // Apply search settings
          if (savedFilters.searchMode) setSearchMode(savedFilters.searchMode);
          if (savedFilters.searchScope) setSearchScope(savedFilters.searchScope);
          if (savedFilters.queryType) setQueryType(savedFilters.queryType);
        } catch (error) {
          console.error('Error parsing saved filters:', error);
        }
      }
      
      // Automatically run the search after loading saved parameters
      setTimeout(() => {
        const searchButton = document.querySelector('.search-button') as HTMLButtonElement;
        if (searchButton) {
          searchButton.click();
        }
      }, 500);
    }
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/search/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Selection handlers
  const handleItemSelect = (itemId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedItems);
    if (isSelected) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleGetSqlDetails = () => {
    // Get PEIDs from selected items
    const selectedPeids = Array.from(selectedItems).map(itemId => {
      const item = allResults.find((r: SearchResult) => r.id === itemId);
      return item?.peid;
    }).filter((peid): peid is number => peid !== undefined);

    if (selectedPeids.length === 0) {
      alert('No valid items selected for SQL details');
      return;
    }

    // Open SQL results page in new tab with state as URL parameters
    const stateData = {
      peids: selectedPeids,
      searchQuery: searchQuery,
      timestamp: Date.now()
    };
    
    // Encode state data as URL parameter
    const encodedState = encodeURIComponent(JSON.stringify(stateData));
    const url = `/sql-results?state=${encodedState}`;
    
    // Open in new tab
    window.open(url, '_blank');
  };

  
  const handleCardClick = (result: SearchResult) => {
    setSelectedResult(result);
    setIsModalOpen(true);
  };
  
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedResult(null);
  };

  const handleClearSearch = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSearchQuery('');
    setAllResults([]);
    setTotalCount(0);
    setCurrentPage(1);
    setHasSearched(false);
    setSelectedItems(new Set());
    setShowSuggestions(false);
    setIsLoading(false);
  };

  const handleSaveSearch = async (searchData: any) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please log in to save searches');
        return;
      }

      await axios.post(`${API_BASE_URL}/api/user/searches`, searchData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowSaveSearchModal(false);
      alert('Search saved successfully!');
    } catch (err: any) {
      console.error('Error saving search:', err);
      alert(err.response?.data?.detail || 'Failed to save search');
    }
  };

  const handleBookmark = async (peid: number, title: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please log in to bookmark items');
        return;
      }

      const isCurrentlyBookmarked = bookmarkedPeids.has(peid);

      if (isCurrentlyBookmarked) {
        // Remove bookmark - need to find bookmark ID first
        const response = await axios.get(`${API_BASE_URL}/api/user/bookmarks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const bookmark = response.data.find((b: any) => b.peid === peid);
        if (bookmark) {
          await axios.delete(`${API_BASE_URL}/api/user/bookmarks/${bookmark.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBookmarkedPeids(prev => {
            const newSet = new Set(prev);
            newSet.delete(peid);
            return newSet;
          });
        }
      } else {
        // Open bookmark modal for new bookmarks
        setBookmarkModalData({ peid, title });
        setShowBookmarkModal(true);
      }
    } catch (err: any) {
      console.error('Error with bookmark:', err);
      alert(err.response?.data?.detail || 'Failed to bookmark item');
    }
  };

  const handleBookmarkCreated = () => {
    // Refresh bookmarks after creating a new one
    loadBookmarks();
    setShowBookmarkModal(false);
    setBookmarkModalData(null);
  };

  // Load bookmarks on component mount
  const loadBookmarks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/user/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const peids = new Set<number>(response.data.map((bookmark: any) => bookmark.peid as number));
      setBookmarkedPeids(peids);
    } catch (err: any) {
      console.error('Error loading bookmarks:', err);
    }
  };

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    // Don't search if query is empty
    if (!searchQuery.trim()) {
      return;
    }
    
    // If this is a new search (not pagination), reset to page 1
    if (e) {
      setCurrentPage(1);
    }
    
    setIsLoading(true);
    setHasSearched(true);

    try {
      // Build query parameters - always fetch 100 records
      const params = new URLSearchParams({
        q: searchQuery.trim(),
        size: '100',
        from: ((currentPage - 1) * 100).toString(),
        search_mode: searchMode,
        search_scope: searchScope,
        query_type: queryType
      });
      
      // Add filters if they're not default values
      if (filters.organization !== 'All') {
        params.append('organization', filters.organization);
      }
      if (filters.account !== 'All') {
        params.append('account', filters.account);
      }
      if (filters.program !== 'All') {
        params.append('program', filters.program);
      }
      if (filters.domain !== 'All') {
        params.append('domain', filters.domain);
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/search?${params}`);
      
      if (currentPage === 1) {
        // First page - replace results
        setAllResults(response.data.results);
      } else {
        // Additional pages - append results
        setAllResults(prev => [...prev, ...response.data.results]);
      }
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error('Search error:', error);
      setAllResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      organization: 'All',
      account: 'All',
      program: 'All',
      domain: 'All'
    });
  };


  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    if (value.length > 2) {
      const mockSuggestions = [
        'Cloud Infrastructure',
        'Healthcare Data',
        'Environmental Assessment',
        'IT Modernization',
        'Research & Development'
      ].filter(s => s.toLowerCase().includes(value.toLowerCase()));
      setSuggestions(mockSuggestions);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  return (
    <div className={`search-page ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar Filters - positioned from top */}
      <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Toggle button */}
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {isSidebarCollapsed ? '→' : '←'}
        </button>

        {/* Collapsed icon menu */}
        <div className="sidebar-icon-menu">
          <div className="sidebar-icon active" title="Search Filters">
            ⚙️
          </div>
          <div className="sidebar-icon" title="Contract Types">
            �
          </div>
          <div className="sidebar-icon" title="Agencies">
            🏛️
          </div>
          <div className="sidebar-icon" title="Budget Range">
            💰
          </div>
        </div>

        {/* Full filters card */}
        <div className="filters-card">
          <div className="filters-title">
            Filters
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear all
            </button>
          </div>

          {/* Organization */}
          <div className="filter-section">
            <div className="filter-label">ORGANIZATION</div>
            <select
              className="filter-select"
              value={filters.organization}
              onChange={(e) => handleFilterChange('organization', e.target.value)}
            >
              <option value="All">All Organizations</option>
              {filterOptions.organizations.map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>
          </div>

          {/* Account */}
          <div className="filter-section">
            <div className="filter-label">ACCOUNT</div>
            <select
              className="filter-select"
              value={filters.account}
              onChange={(e) => handleFilterChange('account', e.target.value)}
            >
              <option value="All">All Accounts</option>
              {filterOptions.accounts.map(account => (
                <option key={account} value={account}>{account}</option>
              ))}
            </select>
          </div>

          {/* Program */}
          <div className="filter-section">
            <div className="filter-label">PROGRAM</div>
            <select
              className="filter-select"
              value={filters.program}
              onChange={(e) => handleFilterChange('program', e.target.value)}
            >
              <option value="All">All Programs</option>
              {filterOptions.programs.map(program => (
                <option key={program} value={program}>{program}</option>
              ))}
            </select>
          </div>

          {/* Domain */}
          <div className="filter-section">
            <div className="filter-label">DOMAIN</div>
            <select
              className="filter-select"
              value={filters.domain}
              onChange={(e) => handleFilterChange('domain', e.target.value)}
            >
              <option value="All">All Domains</option>
              {filterOptions.domains.map(domain => (
                <option key={domain} value={domain}>{domain}</option>
              ))}
            </select>
          </div>

          {/* Selected Items Panel - Moved to Sidebar */}
          <div className={`sidebar-selected-panel ${darkMode ? 'dark-mode' : ''}`}>
            <div className="selected-header">
              <div className="selected-title-row">
                <h4>Selected Items ({selectedItems.size})</h4>
                {selectedItems.size > 0 && (
                  <button 
                    className="btn-secondary-small"
                    onClick={clearSelection}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="selected-items-summary">
              {selectedItems.size === 0 ? (
                <p className="no-selection">No items selected</p>
              ) : (
                Array.from(selectedItems).map(itemId => {
                  const item = allResults.find((r: SearchResult) => r.id === itemId);
                  return item ? (
                    <div key={itemId} className="selected-item-summary">
                      <span className="selected-item-title">{item.title}</span>
                      <button 
                        className="remove-selection"
                        onClick={() => handleItemSelect(itemId, false)}
                        title="Remove from selection"
                      >
                        ×
                      </button>
                    </div>
                  ) : null;
                })
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Header with Search */}
        <div className="search-header">
          {/* Search Container with Single Row Layout */}
          <div className="search-container">
            {/* Single Row: All search controls horizontally */}
            <div className="search-single-row">
              {/* Search Form */}
              <form className="search-form" onSubmit={handleSearch}>
                <div className="search-input-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search anything in the budget..."
                    value={searchQuery}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={() => searchQuery.length > 2 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  />
                  <button 
                    type="submit"
                    className="search-button"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Searching...' : 'Search'}
                  </button>

                  {/* Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {suggestions.map((suggestion, index) => (
                        <div 
                          key={index}
                          className="suggestion-item"
                          onClick={() => {
                            setSearchQuery(suggestion);
                            setShowSuggestions(false);
                          }}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
              
              {/* Search Mode */}
              <div className="control-group">
                <label className="control-label">SEARCH MODE</label>
                <div className="button-group">
                  <button
                    className={`control-button ${searchMode === 'fuzzy' ? 'active' : ''}`}
                    onClick={() => setSearchMode('fuzzy')}
                    type="button"
                  >
                    Fuzzy
                  </button>
                  <button
                    className={`control-button ${searchMode === 'exact' ? 'active' : ''}`}
                    onClick={() => setSearchMode('exact')}
                    type="button"
                  >
                    Exact
                  </button>
                </div>
              </div>
              
              {/* Query Type */}
              <div className="control-group">
                <label className="control-label" title="Controls how multiple search terms are combined">
                  QUERY TYPE
                  <span className="tooltip-icon" title="OR: Finds items containing ANY of your search terms (broader results)&#10;AND: Finds items containing ALL of your search terms (narrower results)&#10;&#10;Example: 'cloud infrastructure'&#10;OR = items with 'cloud' OR 'infrastructure'&#10;AND = items with 'cloud' AND 'infrastructure'">
                    ⓘ
                  </span>
                </label>
                <div className="button-group">
                  <button
                    className={`control-button ${queryType === 'or' ? 'active' : ''}`}
                    onClick={() => setQueryType('or')}
                    type="button"
                    title="Find items containing ANY of your search terms"
                  >
                    OR
                  </button>
                  <button
                    className={`control-button ${queryType === 'and' ? 'active' : ''}`}
                    onClick={() => setQueryType('and')}
                    type="button"
                    title="Find items containing ALL of your search terms"
                  >
                    AND
                  </button>
                </div>
              </div>

              {/* Search Scope */}
              <div className="control-group scope-group">
                <label className="control-label">SEARCH SCOPE</label>
                <div className="button-group">
                  <button
                    className={`control-button ${searchScope === 'all' ? 'active' : ''}`}
                    onClick={() => setSearchScope('all')}
                    type="button"
                  >
                    All
                  </button>
                  <button
                    className={`control-button ${searchScope === 'line_items' ? 'active' : ''}`}
                    onClick={() => setSearchScope('line_items')}
                    type="button"
                  >
                    Line Items
                  </button>
                  <button
                    className={`control-button ${searchScope === 'projects' ? 'active' : ''}`}
                    onClick={() => setSearchScope('projects')}
                    type="button"
                  >
                    Projects
                  </button>
                  <button
                    className={`control-button ${searchScope === 'cost_items' ? 'active' : ''}`}
                    onClick={() => setSearchScope('cost_items')}
                    type="button"
                  >
                    Cost Items
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>

        {/* Results Content */}
        <div className="results-section">
          {/* Results Header - Always visible */}
          <div className={`results-header ${darkMode ? 'dark-mode' : ''}`}>
            <div className="results-info">
              <div className="results-count-row">
                <h2>
                  {!hasSearched ? 'Ready to search' :
                   totalCount === 0 ? 'No hits found' : 
                   `${allResults.length} of ${totalCount.toLocaleString()} hits`}
                </h2>
              </div>
            </div>
            {/* All Action Buttons Grouped Together */}
            <div className="action-buttons-row">
              {/* More Hits Button - Always visible */}
              <UnifiedButton
                variant="outline"
                size="large"
                onClick={() => {
                  setCurrentPage(prev => prev + 1);
                  handleSearch();
                }}
                disabled={!hasSearched || isLoading || allResults.length >= totalCount}
                icon={isLoading ? <MoreHorizontal size={20} /> : <ChevronDown size={20} />}
                title={!hasSearched ? "Search first to load more hits" : 
                       isLoading ? "Loading more results..." : 
                       allResults.length >= totalCount ? "No more hits available" : 
                       "Load more search results"}
              >
                {isLoading ? 'Loading...' : 'More Hits'}
              </UnifiedButton>
              
              {/* Clear Button - Positioned between More Hits and Full Financials */}
              <UnifiedButton
                variant="outline"
                size="large"
                onClick={handleClearSearch}
                disabled={!hasSearched}
                icon={<X size={20} />}
                title={!hasSearched ? "No search to clear" : "Clear search and results"}
              >
                Clear
              </UnifiedButton>
              
              {/* Full Financials Button - Always visible */}
              <UnifiedButton
                variant="outline"
                size="large"
                onClick={handleGetSqlDetails}
                disabled={selectedItems.size === 0}
                title={selectedItems.size > 0 
                  ? `View full financial details for ${selectedItems.size} selected items`
                  : 'Select items to view full financials'}
                icon={<FileBarChart size={20} />}
              >
                Full Financials
              </UnifiedButton>
              
              {/* Save Search Button - Always visible */}
              <UnifiedButton
                variant="outline"
                size="large"
                onClick={() => {
                  console.log('Save Search button clicked');
                  console.log('Current modal state:', showSaveSearchModal);
                  setShowSaveSearchModal(true);
                  console.log('Modal state set to true');
                }}
                disabled={!hasSearched || !searchQuery}
                title={!hasSearched || !searchQuery ? "Perform a search first to save it" : "Save this search for later"}
                icon={<Bookmark size={20} />}
              >
                Save Search
              </UnifiedButton>
            </div>
            <div className="results-controls">
                <div className="results-per-page">
                  <label>Show: </label>
                  <select 
                    value={resultsPerPage} 
                    onChange={(e) => {
                      setResultsPerPage(Number(e.target.value));
                      // No server call - just change client-side display
                    }}
                    className="results-per-page-select"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span> per page</span>
                </div>
                
                {/* View Toggle Buttons */}
                <div className="view-toggle">
                  <button
                    className={`view-btn ${viewMode === 'card' ? 'active' : ''}`}
                    onClick={() => setViewMode('card')}
                    title="Card View"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="6" height="6" rx="1"/>
                      <rect x="9" y="1" width="6" height="6" rx="1"/>
                      <rect x="1" y="9" width="6" height="6" rx="1"/>
                      <rect x="9" y="9" width="6" height="6" rx="1"/>
                    </svg>
                  </button>
                  <button
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="2" width="14" height="2" rx="0.5"/>
                      <rect x="1" y="7" width="14" height="2" rx="0.5"/>
                      <rect x="1" y="12" width="14" height="2" rx="0.5"/>
                    </svg>
                  </button>
                  <div className="theme-divider"></div>
                  <button
                    className={`view-btn theme-btn ${darkMode ? 'dark' : ''}`}
                    onClick={() => setDarkMode(!darkMode)}
                    title={darkMode ? "Light Mode" : "Dark Mode"}
                  >
                    {darkMode ? '☀️' : '🌙'}
                  </button>
                </div>
              </div>
            </div>

          {/* Loading State */}
          {isLoading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Searching contracts...</p>
            </div>
          )}

          {/* Logo Animation - Initial State */}
          {!hasSearched && !isLoading && (
            <div className="logo-animation-container">
              <div className="animated-logo">
                <img 
                  src="/nfi-logo.svg" 
                  alt="NFI Logo" 
                  className="logo-image"
                />
                <div className="logo-pulse-ring"></div>
              </div>
            </div>
          )}

          {/* No Results */}
          {hasSearched && !isLoading && allResults.length === 0 && (
            <div className="no-results">
              <h3>No hits found</h3>
              <p>Try adjusting your search terms or filters to find more opportunities.</p>
            </div>
          )}


          {/* Results Display - Card or List View */}
          {hasSearched && !isLoading && displayedResults.length > 0 && (
            viewMode === 'card' ? (
              <div className={`results-list ${darkMode ? 'dark-mode' : ''}`}>
                {displayedResults.map(result => (
                  <CompactBudgetCard
                    key={result.id}
                    result={result}
                    selected={selectedItems.has(result.id)}
                    onSelect={handleItemSelect}
                    onClick={() => handleCardClick(result)}
                    darkMode={darkMode}
                    onBookmark={handleBookmark}
                    isBookmarked={result.peid ? bookmarkedPeids.has(result.peid) : false}
                  />
                ))}
              </div>
            ) : (
              <div className={`results-table-container ${darkMode ? 'dark-mode' : ''}`}>
                <table className="results-table">
                  <thead>
                    <tr>
                      <th className="checkbox-header"></th>
                      <th>Organization</th>
                      <th>Title & Description</th>
                      <th>Account</th>
                      <th>Program</th>
                      <th>Domain</th>
                      <th>Projects</th>
                      <th className="bookmark-header">Favorite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.map(result => (
                      <BudgetListItem
                        key={result.id}
                        result={result}
                        selected={selectedItems.has(result.id)}
                        onSelect={handleItemSelect}
                        onClick={() => handleCardClick(result)}
                        onBookmark={handleBookmark}
                        isBookmarked={result.peid ? bookmarkedPeids.has(result.peid) : false}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      
      {/* Budget Detail Modal */}
      <BudgetDetailModal
        result={selectedResult}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
      
      {/* Save Search Modal */}
      <SaveSearchModal
        isOpen={showSaveSearchModal}
        onClose={() => setShowSaveSearchModal(false)}
        onSave={handleSaveSearch}
        initialData={{
          query: searchQuery,
          filters: filters,
          searchMode: searchMode,
          searchScope: searchScope,
          queryType: queryType
        }}
      />

      {/* Bookmark Modal */}
      {bookmarkModalData && (
        <BookmarkModal
          peid={bookmarkModalData.peid}
          title={bookmarkModalData.title}
          isOpen={showBookmarkModal}
          onClose={() => {
            setShowBookmarkModal(false);
            setBookmarkModalData(null);
          }}
          onBookmarkCreated={handleBookmarkCreated}
        />
      )}
    </div>
  );
};

export default SearchPage;
